import type { WalletAccountState } from "./signInFlow";
import type { WalletProviderId } from "./types";

/**
 * Connectors — the ONLY wallet-specific code in the sign-in flow.
 *
 * MetaMask, Trust Wallet and WalletConnect all produce the same EIP-191
 * signature and the server has no wallet-specific branch, so everything after
 * "obtain a signer" is shared (see useWalletSignIn.ts). All this module does is
 * hand back an EIP-1193 provider plus the address and chain it is connected to.
 */

/** The slice of EIP-1193 this flow and ethers' BrowserProvider actually use. */
export type Eip1193Provider = {
  request: (args: { method: string; params?: unknown[] | object }) => Promise<unknown>;
  on?: (event: string, listener: (...args: any[]) => void) => void;
  removeListener?: (event: string, listener: (...args: any[]) => void) => void;
};

export type WalletConnection = {
  provider: Eip1193Provider;
  address: string;
  /** Decimal chain id as a string — the shape both wallet endpoints want. */
  chainId: string;
  /**
   * Live re-read of the wallet's current selection.
   *
   * The flow calls this before signing and again before verify: a user who
   * switches account or network mid-attempt would otherwise sign against a
   * challenge bound to the address they just left, which can only come back
   * `signature_mismatch`.
   */
  readAccount: () => Promise<WalletAccountState>;
  /** No-op for injected wallets; ends the relay session for WalletConnect. */
  disconnect: () => Promise<void>;
};

export type ConnectWalletOptions = {
  /** Supported chain ids, used to scope the WalletConnect session proposal. */
  chainIds?: number[];
  /** WalletConnect only: the `wc:` URI to render as a QR / deep link. */
  onUri?: (uri: string) => void;
  /** Abandons a WalletConnect pairing that is still waiting to be scanned. */
  signal?: AbortSignal;
};

export type WalletConnectorErrorReason =
  | "not_installed"
  | "rejected"
  | "cancelled"
  | "no_accounts"
  | "walletconnect_unconfigured"
  | "chains_unavailable"
  | "relay_unreachable"
  | "failed";

export class WalletConnectorError extends Error {
  reason: WalletConnectorErrorReason;

  constructor(reason: WalletConnectorErrorReason, message?: string) {
    super(message ?? reason);
    this.name = "WalletConnectorError";
    this.reason = reason;
  }
}

/* ------------------------------------------------------------------ */
/* Injected providers (MetaMask / Trust Wallet)                        */
/* ------------------------------------------------------------------ */

type Eip6963Detail = {
  info: { uuid: string; name: string; icon: string; rdns: string };
  provider: Eip1193Provider;
};

const INJECTED_RDNS: Record<"metamask" | "trustwallet", string[]> = {
  metamask: ["io.metamask", "io.metamask.flask", "io.metamask.mobile"],
  trustwallet: ["com.trustwallet.app"],
};

/** Legacy pre-EIP-6963 flags. Kept as a fallback, not as the primary path. */
const INJECTED_LEGACY_FLAGS: Record<"metamask" | "trustwallet", string[]> = {
  metamask: ["isMetaMask"],
  trustwallet: ["isTrust", "isTrustWallet"],
};

const announcedProviders = new Map<string, Eip6963Detail>();
let discoveryStarted = false;

/**
 * Starts EIP-6963 discovery. Safe to call repeatedly.
 *
 * Announcements arrive synchronously in response to the request event, but the
 * listener has to be installed first — so this is called on mount rather than
 * at click time, and the click path only re-requests to catch wallets injected
 * after first paint.
 */
export function startInjectedDiscovery(): void {
  if (typeof window === "undefined") return;

  if (!discoveryStarted) {
    discoveryStarted = true;
    window.addEventListener("eip6963:announceProvider", (event: Event) => {
      const detail = (event as CustomEvent<Eip6963Detail>).detail;
      if (detail?.info?.rdns && detail.provider) {
        announcedProviders.set(detail.info.rdns.toLowerCase(), detail);
      }
    });
  }

  window.dispatchEvent(new Event("eip6963:requestProvider"));
}

function findAnnouncedProvider(target: "metamask" | "trustwallet"): Eip1193Provider | null {
  for (const rdns of INJECTED_RDNS[target]) {
    const match = announcedProviders.get(rdns);
    if (match) return match.provider;
  }
  return null;
}

function findLegacyProvider(target: "metamask" | "trustwallet"): Eip1193Provider | null {
  const injected = (window as any).ethereum;
  if (!injected) return null;

  const flags = INJECTED_LEGACY_FLAGS[target];
  // Several wallets inject into `window.ethereum.providers` when more than one
  // extension is installed; the bare object is the single-wallet case.
  const candidates: any[] = Array.isArray(injected.providers) ? injected.providers : [injected];
  const flagged = candidates.find((candidate) => flags.some((flag) => candidate?.[flag]));
  if (flagged) return flagged as Eip1193Provider;

  // Only one injected provider and it claims no flag we recognise: it is still
  // the wallet the user has, and it signs EIP-191 like any other.
  return candidates.length === 1 ? (candidates[0] as Eip1193Provider) : null;
}

export function getInjectedProvider(target: "metamask" | "trustwallet"): Eip1193Provider | null {
  if (typeof window === "undefined") return null;
  return findAnnouncedProvider(target) ?? findLegacyProvider(target);
}

/**
 * Where to send a mobile visitor with no injected provider: the wallet's own
 * in-app browser, which does inject one, re-entering this same flow.
 */
export function getWalletOpenUrl(target: "metamask" | "trustwallet"): string | null {
  if (typeof window === "undefined") return null;
  const { host, pathname, search, href } = window.location;

  if (target === "metamask") return `https://metamask.app.link/dapp/${host}${pathname}${search}`;
  if (target === "trustwallet") {
    return `https://link.trustwallet.com/open_url?coin_id=60&url=${encodeURIComponent(href)}`;
  }
  return null;
}

function toDecimalChainId(raw: unknown): string {
  // eth_chainId is a hex quantity per the JSON-RPC spec, but WalletConnect's
  // own eip155 provider answers it with a plain number.
  if (typeof raw === "number") return String(raw);
  const value = String(raw ?? "");
  const parsed = value.startsWith("0x") ? Number.parseInt(value, 16) : Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? String(parsed) : "";
}

/**
 * One implementation for both connectors.
 *
 * `eth_accounts` and `eth_chainId` are answered locally by an injected wallet
 * and, for WalletConnect, out of the approved session by its own eip155
 * provider — so neither costs a network round trip, and the flow can afford to
 * re-check before signing and again before verify.
 */
async function readAccountFrom(provider: Eip1193Provider): Promise<WalletAccountState> {
  const [accounts, chainId] = await Promise.all([
    provider.request({ method: "eth_accounts" }) as Promise<string[]>,
    provider.request({ method: "eth_chainId" }),
  ]);

  return {
    // An empty address (locked wallet, revoked permission) is deliberately not
    // an error here — the flow reads it as "no longer the bound account".
    address: accounts?.[0] ?? "",
    chainId: toDecimalChainId(chainId),
  };
}

async function connectInjected(target: "metamask" | "trustwallet"): Promise<WalletConnection> {
  startInjectedDiscovery();
  const provider = getInjectedProvider(target);
  if (!provider) throw new WalletConnectorError("not_installed");

  let accounts: string[];
  try {
    accounts = (await provider.request({ method: "eth_requestAccounts" })) as string[];
  } catch (error) {
    // 4001 is the EIP-1193 "user rejected request" code.
    throw new WalletConnectorError((error as any)?.code === 4001 ? "rejected" : "failed");
  }

  if (!accounts?.length) throw new WalletConnectorError("no_accounts");

  const chainId = toDecimalChainId(await provider.request({ method: "eth_chainId" }));

  return {
    provider,
    address: accounts[0],
    chainId,
    readAccount: () => readAccountFrom(provider),
    // An injected provider's permission is owned by the extension/app, not by
    // this page — there is nothing to tear down.
    disconnect: async () => {},
  };
}

/* ------------------------------------------------------------------ */
/* WalletConnect                                                       */
/* ------------------------------------------------------------------ */

export function getWalletConnectProjectId(): string {
  return process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID?.trim() || "";
}

/** WalletConnect cannot pair without a project id, so its button is hidden
 *  rather than offered and then failing at click time. */
export function isWalletConnectConfigured(): boolean {
  return getWalletConnectProjectId().length > 0;
}

/**
 * How long the relay handshake gets before the attempt is abandoned.
 *
 * This bounds only the part we control. Once a pairing URI is on screen the
 * clock belongs to the user, who may take as long as they like to scan it.
 */
const RELAY_HANDSHAKE_TIMEOUT_MS = 20_000;

/**
 * Rejects if the relay hasn't produced a pairing URI in time.
 *
 * A wrong project id or an unreachable relay leaves the pairing retrying with
 * backoff and never settling, which would leave the sign-in buttons disabled
 * behind a spinner with no way back. `isSatisfied` is what drops the deadline:
 * once the QR is on screen the wait is the user's, not ours, and it must not
 * be cut short. Never resolves — it is only ever raced against the real work.
 */
function relayDeadline(isSatisfied: () => boolean, settled: Promise<unknown>): Promise<never> {
  return new Promise<never>((_, reject) => {
    const timer = setTimeout(() => {
      if (!isSatisfied()) reject(new WalletConnectorError("relay_unreachable"));
    }, RELAY_HANDSHAKE_TIMEOUT_MS);

    const clear = () => clearTimeout(timer);
    settled.then(clear, clear);
  });
}

async function connectWalletConnect({
  chainIds,
  onUri,
  signal,
}: ConnectWalletOptions): Promise<WalletConnection> {
  const projectId = getWalletConnectProjectId();
  if (!projectId) throw new WalletConnectorError("walletconnect_unconfigured");

  // The session proposal has to name the chains it wants approved, and the
  // only authoritative list is the backend's /networks catalogue (the same one
  // the wallet page and balance popover use). Hardcoding a mainnet fallback
  // here would be this feature inventing its own chain list, so an unloaded
  // catalogue is surfaced instead of guessed around.
  if (!chainIds?.length) throw new WalletConnectorError("chains_unavailable");

  // Lazy import for the same reason ethers is never imported statically here
  // (see features/wallet/utils.ts): this pulls a large relay + crypto bundle
  // that must not enter the layout/navbar client-chunk graph.
  const { UniversalProvider } = await import("@walletconnect/universal-provider");

  const initPromise = UniversalProvider.init({
    projectId,
    metadata: {
      name: "NFTBaz",
      description: "NFTBaz marketplace",
      url: window.location.origin,
      icons: [`${window.location.origin}/og-image.png`],
    },
  });
  const provider = await Promise.race([
    initPromise,
    relayDeadline(() => false, initPromise),
  ]);

  if (signal?.aborted) {
    await provider.disconnect().catch(() => {});
    throw new WalletConnectorError("cancelled");
  }

  const chains = chainIds.map((id) => `eip155:${id}`);

  let pairingUriSeen = false;
  const forwardUri = (uri: string) => {
    pairingUriSeen = true;
    onUri?.(uri);
  };
  const abortPairing = () => provider.abortPairingAttempt();

  provider.on("display_uri", forwardUri);
  signal?.addEventListener("abort", abortPairing, { once: true });

  try {
    const connectPromise = provider.connect({
      // `optionalNamespaces`, not `namespaces`: a wallet that supports only a
      // subset of our chains should still be able to approve the session.
      optionalNamespaces: {
        eip155: {
          // personal_sign is the only method this flow ever calls — the
          // marketplace never asks a user's wallet to sign a transaction.
          methods: ["personal_sign"],
          chains,
          events: ["chainChanged", "accountsChanged"],
        },
      },
    });

    // Bounded only until the QR exists. After that the deadline is dropped and
    // the user can take as long as they need — or close the dialog, which
    // aborts through `signal`.
    await Promise.race([
      connectPromise,
      relayDeadline(() => pairingUriSeen, connectPromise),
    ]);
  } catch (error) {
    provider.abortPairingAttempt();
    await provider.disconnect().catch(() => {});
    if (signal?.aborted) throw new WalletConnectorError("cancelled");
    if (error instanceof WalletConnectorError) throw error;
    throw new WalletConnectorError((error as any)?.code === 4001 ? "rejected" : "failed");
  } finally {
    provider.removeListener("display_uri", forwardUri);
    signal?.removeEventListener("abort", abortPairing);
  }

  // CAIP-10: "eip155:137:0xabc…"
  const account = provider.session?.namespaces?.eip155?.accounts?.[0];
  const [, sessionChainId, address] = account?.split(":") ?? [];

  if (!address) {
    await provider.disconnect().catch(() => {});
    throw new WalletConnectorError("no_accounts");
  }

  provider.setDefaultChain(`eip155:${sessionChainId}`);

  const eip1193 = provider as unknown as Eip1193Provider;

  return {
    provider: eip1193,
    address,
    chainId: toDecimalChainId(sessionChainId),
    readAccount: () => readAccountFrom(eip1193),
    disconnect: async () => {
      await provider.disconnect().catch(() => {});
    },
  };
}

/**
 * The one entry point. Everything downstream treats all three wallets
 * identically.
 */
export async function connectWallet(
  target: WalletProviderId,
  options: ConnectWalletOptions = {},
): Promise<WalletConnection> {
  if (target === "walletconnect") return connectWalletConnect(options);
  return connectInjected(target);
}
